/**
 * エントリポイント。
 * 入力 → プレイヤーの物理更新 → カメラ → 照準 → 描画、というゲームループと、
 * WebSocket（§5）で届く world / プレイヤー状態の反映だけを担当する。
 */

import './style.css';
import type { BlockTextureDefinition } from '../shared/block-textures';
import type { BraceEntity, Entity, PeerInfo, PlayerState, ServerMessage } from '../shared/types';
import { ClothSimDialog } from './cloth-sim-dialog';
import { resolveMaterial } from '../shared/materials';
import { PlayerAvatars } from './avatars';
import {
  BootSession,
  formatBootDebug,
  formatBootMenu,
  logBootReport,
  type BootReport,
} from './boot-timing';
import { Builder, EMPTY_SLOT_INDEX, createBuildSlot, toolSlotLabel, type SlotConfig } from './build';
import { RoomConnection, type ConnectionStatus, type MessageMeta } from './connection';
import { Hud } from './hud';
import { InputController, clampPitch, type InputAction } from './input';
import { MobileControls } from './mobile-controls';
import { prefersTouchPlay } from './touch-play';
import { LocalPlayer } from './player';
import { WorldScene } from './scene';
import { DEFAULT_GRAPHICS_QUALITY, resolveGraphicsQuality } from './graphics-quality';
import {
  GI_DIFFUSE_GAIN,
  GI_DIFFUSE_GAIN_MAX,
  GI_DIFFUSE_GAIN_MIN,
  GI_DISTANCE_AMBIENT,
  GI_DISTANCE_AMBIENT_MAX,
  GI_DISTANCE_AMBIENT_MIN,
  GI_NEAR_AMBIENT,
  GI_NEAR_AMBIENT_MAX,
  GI_NEAR_AMBIENT_MIN,
  readGiSlider,
} from '../shared/restir-gi-sampling';
import { DEFAULT_RESTIR_DENOISER_MODE, resolveRestirDenoiserMode } from './restir-denoiser';
import {
  DEFAULT_RESTIR_CHEAP_CANDIDATE_VISIBILITY,
  resolveRestirFlag,
} from './restir-sampling';
import { GridEditor } from './grid-editor';
import {
  DENOISER_MODE_PATHS,
  LOCALE_STORAGE_KEY,
  detectLocale,
  materialLabel,
  objectLabel,
  setLocale,
  t,
} from './i18n';
import { DEFAULT_ROOM, ROOM_ID_PATTERN, canJoinRoomAsGuest } from '../shared/rooms';

const NAME_STORAGE_KEY = 'mapeditor:name';
const SENSITIVITY_STORAGE_KEY = 'mapeditor:sensitivity';
const INVERT_HOTBAR_SCROLL_STORAGE_KEY = 'mapeditor:invert-hotbar-scroll';
const GRAPHICS_STORAGE_KEY = 'mapeditor:graphics';
const DOF_STORAGE_KEY = 'mapeditor:dof';
const SDF_SHADOWS_STORAGE_KEY = 'mapeditor:sdf-shadows';
const SUN_SDF_SHADOWS_STORAGE_KEY = 'mapeditor:sdf-sun-shadows';
const DI_SDF_SHADOWS_STORAGE_KEY = 'mapeditor:sdf-di-shadows';
const GI_SDF_SHADOWS_STORAGE_KEY = 'mapeditor:sdf-gi-shadows';
const SDF_ANALYTIC_STORAGE_KEY = 'mapeditor:sdf-analytic';
const SDF_ANALYTIC_BVH_STORAGE_KEY = 'mapeditor:sdf-analytic-bvh';
const SDF_VOXEL_SPHERES_STORAGE_KEY = 'mapeditor:sdf-voxel-spheres';
const SUNLIGHT_STORAGE_KEY = 'mapeditor:sunlight';
const AMBIENT_STORAGE_KEY = 'mapeditor:ambient';
const DENOISER_STORAGE_KEY = 'mapeditor:denoiser';
const RESTIR_CHEAP_VISIBILITY_STORAGE_KEY = 'mapeditor:restir-cheap-visibility';
const COLLISION_DEBUG_STORAGE_KEY = 'mapeditor:collision-debug';
const GI_DIFFUSE_GAIN_STORAGE_KEY = 'mapeditor:gi-diffuse-gain';
const GI_DISTANCE_AMBIENT_STORAGE_KEY = 'mapeditor:gi-distance-ambient';
const GI_NEAR_AMBIENT_STORAGE_KEY = 'mapeditor:gi-near-ambient';
const SLOT_STORAGE_KEY = 'mapeditor:hotbar:v3';
const SLOT_INDEX_STORAGE_KEY = 'mapeditor:hotbar-slot:v1';
const POSE_STORAGE_KEY = 'mapeditor:pose:v1';
const DEFAULT_SPAWN = { x: 0.5, y: 0, z: 6.5 };
/** 姿勢を localStorage へ書き出す最短間隔（ms） */
const POSE_SAVE_INTERVAL = 1000;

interface StoredPose {
  position: { x: number; y: number; z: number };
  yaw: number;
  pitch: number;
  /** 省略時は false（古い保存データ互換） */
  flying?: boolean;
}

/** 自分の状態を送る間隔（ms） */
const STATE_INTERVAL = 100;
/** 動きがなくても送るキープアライブ間隔（ms） */
const STATE_KEEPALIVE = 1000;
/** クリック長押しで連続設置・連続破壊する間隔（ms） */
const REPEAT_INTERVAL = 200;

const canvas = document.getElementById('viewport') as HTMLCanvasElement;

setLocale(
  detectLocale({
    stored: window.localStorage.getItem(LOCALE_STORAGE_KEY),
    languages: navigator.languages,
  })
);

/* ------------------------------------------------------------------ */
/* 構成要素                                                            */
/* ------------------------------------------------------------------ */

const storedGraphics = window.localStorage.getItem(GRAPHICS_STORAGE_KEY);
const sceneStartedAt = performance.now();
const scene = new WorldScene(canvas, resolveGraphicsQuality(storedGraphics));
let boot = new BootSession('reload');
boot.recordModule(sceneStartedAt);
boot.recordScene(performance.now() - sceneStartedAt);
const player = new LocalPlayer(DEFAULT_SPAWN);
const avatars = new PlayerAvatars(scene.scene, (material) => scene.setupLitMaterial(material));
const peers = new Map<string, PeerInfo>();
const roomTextures = new Map<string, BlockTextureDefinition>();
const pendingTextures = new Map<string, { slotIndex: number; previous: SlotConfig }>();

let self: PeerInfo | null = null;
let connection: RoomConnection | null = null;
let room = currentRoom();
let playerName = storedName();
let builder: Builder;
/** ワールド未着のあいだは物理を止める（リロード直後の落下で保存位置を壊さない） */
let worldReady = false;
/**
 * 接続完了時に再適用する姿勢。
 * 起動／ルーム移動でセットし、最初の init_state で消費する。
 * 再接続時は null のままにして、読み込み中の現在位置を維持する。
 */
let poseToRestore: StoredPose | null = null;
let lastBoot: BootReport | null = null;
applyStoredPose(room);

const uiStartedAt = performance.now();
const hud = new Hud({
  onPlay: () => input.requestLock(),
  onView: () => hud.setMode('viewing'),
  onGraphics: () => hud.setMode('graphics'),
  onControls: () => hud.setMode('controls'),
  onModeChange: (mode) => scene.setMenuLite(mode === 'menu'),
  onRoomChange: (next) => changeRoom(next),
  onNameChange: (next) => changeName(next),
  onSensitivityChange: (value) => {
    input.sensitivity = value;
    window.localStorage.setItem(SENSITIVITY_STORAGE_KEY, String(value));
  },
  onInvertHotbarScrollChange: (enabled) => {
    input.invertHotbarScroll = enabled;
    window.localStorage.setItem(INVERT_HOTBAR_SCROLL_STORAGE_KEY, enabled ? '1' : '0');
    hud.showItemName(
      t('item.invertHotbarScroll', { label: enabled ? t('graphics.on') : t('graphics.off') })
    );
  },
  onGraphicsQualityChange: (id) => {
    scene.setGraphicsQuality(id);
    window.localStorage.setItem(GRAPHICS_STORAGE_KEY, id);
    hud.showItemName(
      t('item.graphicsQuality', {
        label:
          id === 'low' ? t('graphics.low') : id === 'medium' ? t('graphics.medium') : t('graphics.high'),
      })
    );
  },
  onDepthOfFieldChange: (enabled) => {
    scene.depthOfField.enabled = enabled;
    window.localStorage.setItem(DOF_STORAGE_KEY, enabled ? '1' : '0');
    hud.showItemName(t('item.depthOfField', { label: enabled ? t('graphics.on') : t('graphics.off') }));
  },
  onSunSdfShadowsChange: (enabled) => {
    scene.restir.sunSdfShadowsEnabled = enabled;
    window.localStorage.setItem(SUN_SDF_SHADOWS_STORAGE_KEY, enabled ? '1' : '0');
    hud.showItemName(t('item.sunSdfShadows', { label: enabled ? t('graphics.on') : t('graphics.off') }));
  },
  onDiSdfShadowsChange: (enabled) => {
    scene.restir.diSdfShadowsEnabled = enabled;
    window.localStorage.setItem(DI_SDF_SHADOWS_STORAGE_KEY, enabled ? '1' : '0');
    hud.showItemName(t('item.diSdfShadows', { label: enabled ? t('graphics.on') : t('graphics.off') }));
  },
  onGiSdfShadowsChange: (enabled) => {
    scene.restirGi.sdfShadowsEnabled = enabled;
    window.localStorage.setItem(GI_SDF_SHADOWS_STORAGE_KEY, enabled ? '1' : '0');
    hud.showItemName(t('item.giSdfShadows', { label: enabled ? t('graphics.on') : t('graphics.off') }));
  },
  onSdfAnalyticChange: (enabled) => {
    scene.analyticSdfEnabled = enabled;
    window.localStorage.setItem(SDF_ANALYTIC_STORAGE_KEY, enabled ? '1' : '0');
    hud.showItemName(t('item.sdfAnalytic', { label: enabled ? t('graphics.on') : t('graphics.off') }));
  },
  onSdfAnalyticBvhChange: (enabled) => {
    scene.analyticBvhEnabled = enabled;
    window.localStorage.setItem(SDF_ANALYTIC_BVH_STORAGE_KEY, enabled ? '1' : '0');
    hud.showItemName(t('item.sdfAnalyticBvh', { label: enabled ? t('graphics.on') : t('graphics.off') }));
  },
  onSdfVoxelSpheresChange: (enabled) => {
    scene.voxelSpheresEnabled = enabled;
    window.localStorage.setItem(SDF_VOXEL_SPHERES_STORAGE_KEY, enabled ? '1' : '0');
    hud.showItemName(t('item.sdfVoxelSpheres', { label: enabled ? t('graphics.on') : t('graphics.off') }));
  },
  onSunlightChange: (enabled) => {
    scene.restir.sunEnabled = enabled;
    window.localStorage.setItem(SUNLIGHT_STORAGE_KEY, enabled ? '1' : '0');
    hud.showItemName(t('item.sunlight', { label: enabled ? t('graphics.on') : t('graphics.off') }));
  },
  onAmbientChange: (enabled) => {
    scene.restir.ambientEnabled = enabled;
    window.localStorage.setItem(AMBIENT_STORAGE_KEY, enabled ? '1' : '0');
    hud.showItemName(t('item.ambient', { label: enabled ? t('graphics.on') : t('graphics.off') }));
  },
  onGiDiffuseGainChange: (value) => {
    scene.restirGi.diffuseGain = value;
    window.localStorage.setItem(GI_DIFFUSE_GAIN_STORAGE_KEY, String(scene.restirGi.diffuseGain));
    hud.showItemName(t('item.giDiffuseGain', { label: scene.restirGi.diffuseGain.toFixed(1) }));
  },
  onGiDistanceAmbientChange: (value) => {
    scene.restir.distanceAmbient = value;
    window.localStorage.setItem(GI_DISTANCE_AMBIENT_STORAGE_KEY, String(scene.restir.distanceAmbient));
    hud.showItemName(t('item.giDistanceAmbient', { label: scene.restir.distanceAmbient.toFixed(2) }));
  },
  onGiNearAmbientChange: (value) => {
    scene.restir.nearAmbient = value;
    window.localStorage.setItem(GI_NEAR_AMBIENT_STORAGE_KEY, String(scene.restir.nearAmbient));
    hud.showItemName(t('item.giNearAmbient', { label: scene.restir.nearAmbient.toFixed(2) }));
  },
  onDenoiserChange: (mode) => {
    scene.restir.denoiserMode = mode;
    window.localStorage.setItem(DENOISER_STORAGE_KEY, mode);
    hud.showItemName(t('item.denoiser', { label: t(DENOISER_MODE_PATHS[mode]) }));
  },
  onRestirCheapVisibilityChange: (enabled) => {
    scene.restir.cheapCandidateVisibility = enabled;
    window.localStorage.setItem(RESTIR_CHEAP_VISIBILITY_STORAGE_KEY, enabled ? '1' : '0');
    hud.showItemName(
      t('item.restirCheapVisibility', { label: enabled ? t('graphics.on') : t('graphics.off') })
    );
  },
  onCollisionDebugChange: (enabled) => {
    scene.collisionDebug = enabled;
    window.localStorage.setItem(COLLISION_DEBUG_STORAGE_KEY, enabled ? '1' : '0');
    hud.showItemName(
      t('item.collisionDebug', { label: enabled ? t('graphics.on') : t('graphics.off') })
    );
  },
  onGraphicsReset: () => {
    scene.setGraphicsQuality(DEFAULT_GRAPHICS_QUALITY);
    scene.depthOfField.enabled = false;
    scene.restir.sunSdfShadowsEnabled = true;
    scene.restir.diSdfShadowsEnabled = true;
    scene.restirGi.sdfShadowsEnabled = true;
    scene.analyticSdfEnabled = true;
    scene.analyticBvhEnabled = true;
    scene.voxelSpheresEnabled = true;
    scene.restir.sunEnabled = true;
    scene.restir.ambientEnabled = true;
    scene.restirGi.diffuseGain = GI_DIFFUSE_GAIN;
    scene.restir.distanceAmbient = GI_DISTANCE_AMBIENT;
    scene.restir.nearAmbient = GI_NEAR_AMBIENT;
    scene.restir.denoiserMode = DEFAULT_RESTIR_DENOISER_MODE;
    scene.restir.cheapCandidateVisibility = DEFAULT_RESTIR_CHEAP_CANDIDATE_VISIBILITY;
    scene.collisionDebug = false;

    window.localStorage.setItem(GRAPHICS_STORAGE_KEY, DEFAULT_GRAPHICS_QUALITY);
    window.localStorage.setItem(DOF_STORAGE_KEY, '0');
    window.localStorage.setItem(SUN_SDF_SHADOWS_STORAGE_KEY, '1');
    window.localStorage.setItem(DI_SDF_SHADOWS_STORAGE_KEY, '1');
    window.localStorage.setItem(GI_SDF_SHADOWS_STORAGE_KEY, '1');
    window.localStorage.setItem(SDF_ANALYTIC_STORAGE_KEY, '1');
    window.localStorage.setItem(SDF_ANALYTIC_BVH_STORAGE_KEY, '1');
    window.localStorage.setItem(SDF_VOXEL_SPHERES_STORAGE_KEY, '1');
    window.localStorage.setItem(SUNLIGHT_STORAGE_KEY, '1');
    window.localStorage.setItem(AMBIENT_STORAGE_KEY, '1');
    window.localStorage.setItem(GI_DIFFUSE_GAIN_STORAGE_KEY, String(GI_DIFFUSE_GAIN));
    window.localStorage.setItem(GI_DISTANCE_AMBIENT_STORAGE_KEY, String(GI_DISTANCE_AMBIENT));
    window.localStorage.setItem(GI_NEAR_AMBIENT_STORAGE_KEY, String(GI_NEAR_AMBIENT));
    window.localStorage.setItem(DENOISER_STORAGE_KEY, DEFAULT_RESTIR_DENOISER_MODE);
    window.localStorage.setItem(
      RESTIR_CHEAP_VISIBILITY_STORAGE_KEY,
      DEFAULT_RESTIR_CHEAP_CANDIDATE_VISIBILITY ? '1' : '0'
    );
    window.localStorage.setItem(COLLISION_DEBUG_STORAGE_KEY, '0');

    hud.setGraphicsQuality(DEFAULT_GRAPHICS_QUALITY);
    hud.setDepthOfField(false);
    hud.setSunSdfShadows(true);
    hud.setDiSdfShadows(true);
    hud.setGiSdfShadows(true);
    hud.setSdfAnalytic(true);
    hud.setSdfAnalyticBvh(true);
    hud.setSdfVoxelSpheres(true);
    hud.setSunlight(true);
    hud.setAmbient(true);
    hud.setGiDiffuseGain(GI_DIFFUSE_GAIN);
    hud.setGiDistanceAmbient(GI_DISTANCE_AMBIENT);
    hud.setGiNearAmbient(GI_NEAR_AMBIENT);
    hud.setDenoiser(DEFAULT_RESTIR_DENOISER_MODE);
    hud.setRestirCheapVisibility(DEFAULT_RESTIR_CHEAP_CANDIDATE_VISIBILITY);
    hud.setCollisionDebug(false);
    hud.toast(t('toast.graphicsReset'));
  },
  onSlotChange: (index, config) => builder.setSlotConfig(index, config),
  onTextureCreate: (texture, slotIndex) => createRoomTexture(texture, slotIndex),
});

{
  const requested = requestedRoom();
  if (requested && !canJoinRoomAsGuest(requested)) {
    const url = new URL(window.location.href);
    url.searchParams.delete('room');
    window.history.replaceState(null, '', url);
    hud.toast(t('toast.roomRequiresLogin'), 'error');
  }
}

let pendingUnlockMode: 'inventory' | 'graphics' | 'grid-editor' | 'cloth-sim' | null = null;
let gridEditor: GridEditor;
let clothSimDialog: ClothSimDialog;
let input: InputController;

builder = new Builder({
  scene,
  player,
  send: (message) => connection?.send(message) ?? false,
  onError: (message) => hud.toast(message, 'error'),
  onNotice: (message) => hud.toast(message),
  onChange: () => hud.renderBuilder(builder),
  onSlotsChange: (slots) => {
    window.localStorage.setItem(SLOT_STORAGE_KEY, JSON.stringify(slots));
  },
  onSlotChange: (index) => {
    window.sessionStorage.setItem(SLOT_INDEX_STORAGE_KEY, String(index));
  },
  onOpenClothSim: (entity) => openClothSim(entity),
  slots: storedSlots(),
  slot: storedSlotIndex(),
});

input = new InputController(canvas, {
  onLockChange: (locked) => {
    if (locked) {
      pendingUnlockMode = null;
      hud.setMode('playing');
      return;
    }
    // E / V / G で明示的に解除した場合だけ各ダイアログへ。通常の Esc はポーズメニュー。
    const unlockedMode = pendingUnlockMode ?? 'menu';
    pendingUnlockMode = null;
    hud.setMode(unlockedMode);
  },
  onAction: (action, value) => handleAction(action, value),
});

new MobileControls(
  canvas,
  input,
  document.getElementById('mobile-stick') as HTMLElement,
  document.getElementById('mobile-jump') as HTMLElement
);

gridEditor = new GridEditor({
  getState: () => scene.viewGrid.current,
  onChange: (state) => scene.viewGrid.setState(state),
  onClose: () => hud.setMode('playing'),
});

clothSimDialog = new ClothSimDialog({
  onApply: (entityId, bake) => {
    builder.applyClothBake(entityId, bake);
    hud.setMode('playing');
    input.requestLock();
  },
  onClose: () => hud.setMode('playing'),
});

function openClothSim(entity: BraceEntity): void {
  pendingUnlockMode = 'cloth-sim';
  clothSimDialog.open(entity);
  input.releaseLock();
}

/* ------------------------------------------------------------------ */
/* 入力アクション                                                      */
/* ------------------------------------------------------------------ */

let nextUseAt = 0;

function handleAction(action: InputAction, value?: number): void {
  switch (action) {
    case 'use':
      builder.use();
      nextUseAt = performance.now() + REPEAT_INTERVAL;
      return;
    case 'pick':
      builder.pickMaterial();
      announceHeldItem();
      return;
    case 'hotbar':
      builder.setSlot(value ?? 0);
      announceHeldItem();
      return;
    case 'hotbar-delta':
      builder.cycleSlot(value ?? 1);
      announceHeldItem();
      return;
    case 'hotbar-destroy':
      if (!builder.selectDestroyTool()) {
        hud.toast(t('toast.noDestroyTool'));
        return;
      }
      announceHeldItem();
      return;
    case 'hotbar-previous':
      builder.restorePreviousSlot();
      announceHeldItem();
      return;
    case 'thickness':
      builder.adjustThickness(value ?? 1);
      announceHeldItem();
      return;
    case 'grid-editor':
      pendingUnlockMode = 'grid-editor';
      gridEditor.open();
      input.releaseLock();
      return;
    case 'commit':
      builder.commit();
      return;
    case 'undo-point':
      builder.undoPoint();
      return;
    case 'cancel':
      builder.cancel();
      announceHeldItem();
      return;
    case 'toggle-fly':
      player.toggleFly();
      hud.showItemName(player.flying ? t('item.flyOn') : t('item.flyOff'));
      return;
    case 'toggle-debug':
      hud.toggleDebug();
      return;
    case 'inventory':
      if (prefersTouchPlay()) return;
      pendingUnlockMode = 'inventory';
      input.releaseLock();
      return;
    case 'graphics':
      pendingUnlockMode = 'graphics';
      input.releaseLock();
      return;
    case 'playerlist':
      hud.setPlayerListVisible(value === 1);
      return;
  }
}

function announceHeldItem(): void {
  const config = builder.getSlotConfig(builder.slot);
  if (config.kind === 'empty') {
    hud.showItemName(t('tool.empty'));
  } else if (config.kind === 'tool') {
    hud.showItemName(toolSlotLabel(config));
  } else if (config.kind === 'placeable') {
    hud.showItemName(
      t('item.held', {
        shape: t('shape.object'),
        label: objectLabel(config.definitionId),
        summary: builder.shapeSummary(),
      })
    );
  } else if (config.kind === 'clipboard') {
    hud.showItemName(
      t('item.held', {
        shape: t('tool.clipboard'),
        label: t('tool.clipboardCount', { count: config.items.length }),
        summary: builder.shapeSummary(),
      })
    );
  } else {
    const label =
      roomTextures.get(config.material)?.name ??
      materialLabel(config.material, resolveMaterial(config.material).label);
    hud.showItemName(
      t('item.held', {
        shape: t(
          config.shape.kind === 'brace'
            ? 'shape.brace'
            : config.shape.kind === 'stair'
              ? 'shape.stair'
              : config.shape.kind === 'polygon'
                ? 'shape.polygon'
                : 'shape.plane'
        ),
        label,
        summary: builder.shapeSummary(),
      })
    );
  }
  hud.renderBuilder(builder);
}

function replaceRoomTextures(textures: readonly BlockTextureDefinition[]): void {
  roomTextures.clear();
  for (const texture of textures) roomTextures.set(texture.id, texture);
  scene.setBlockTextures(textures);
  hud.setBlockTextures(textures);
}

function registerRoomTexture(texture: BlockTextureDefinition): void {
  roomTextures.set(texture.id, texture);
  scene.setBlockTexture(texture);
  hud.setBlockTextures(roomTextures.values());
}

function createRoomTexture(texture: BlockTextureDefinition, slotIndex: number): boolean {
  if (!connection?.send({ type: 'create_block_texture', texture })) {
    hud.toast(t('toast.notConnected'), 'error');
    return false;
  }
  if (slotIndex !== EMPTY_SLOT_INDEX) {
    pendingTextures.set(texture.id, {
      slotIndex,
      previous: builder.getSlotConfig(slotIndex),
    });
    builder.setSlotConfig(slotIndex, createBuildSlot(texture.id, 'plane'));
  }
  registerRoomTexture(texture);
  hud.toast(t('toast.creatingTexture', { name: texture.name }));
  return true;
}

function rollbackPendingTexture(id: string): void {
  const pending = pendingTextures.get(id);
  if (!pending) return;
  pendingTextures.delete(id);
  roomTextures.delete(id);
  scene.removeBlockTexture(id);
  hud.setBlockTextures(roomTextures.values());
  builder.setSlotConfig(pending.slotIndex, pending.previous);
}

/* ------------------------------------------------------------------ */
/* サーバーメッセージ                                                  */
/* ------------------------------------------------------------------ */

function handleMessage(message: ServerMessage, meta?: MessageMeta): void {
  switch (message.type) {
    case 'init_state': {
      boot.recordInitMessage(meta?.parseMs ?? 0, meta?.bytes ?? 0);
      scene.clearEntities();
      avatars.clear();
      peers.clear();
      const confirmedTextureIds = new Set(message.textures.map((texture) => texture.id));
      for (const [id, pending] of pendingTextures) {
        if (!confirmedTextureIds.has(id)) {
          builder.setSlotConfig(pending.slotIndex, pending.previous);
        }
      }
      pendingTextures.clear();
      replaceRoomTextures(message.textures);
      self = message.self;
      for (const peer of message.peers) peers.set(peer.playerId, peer);
      for (const entity of message.entities) {
        if (isSupportedEntity(entity)) scene.setEntity(entity);
      }
      // 保存姿勢をワールド到着後にもう一度適用（読み込み待ち中のズレを打ち消す）
      if (poseToRestore) {
        player.setPose(
          poseToRestore.position,
          poseToRestore.yaw,
          poseToRestore.pitch,
          poseToRestore.flying
        );
        poseToRestore = null;
      }
      // スポーン／保存位置にコライダがあると埋まるので逃がす
      player.ensureClear(scene.collision);
      worldReady = true;
      if (import.meta.env.DEV) {
        const debug = (window as unknown as { __mapeditorDebug?: { lastReadyPose?: unknown } })
          .__mapeditorDebug;
        if (debug) {
          debug.lastReadyPose = {
            ...player.position,
            yaw: player.yaw,
            pitch: player.pitch,
            flying: player.flying,
          };
        }
      }
      hud.renderPlayers(self, peers.values());
      boot.recordWorldReady();
      return;
    }
    case 'create_block_texture': {
      const wasPending = pendingTextures.delete(message.texture.id);
      registerRoomTexture(message.texture);
      if (wasPending) hud.toast(t('toast.createdTexture', { name: message.texture.name }));
      else hud.toast(t('toast.textureAdded', { name: message.texture.name }));
      return;
    }
    case 'place_entity':
    case 'update_entity': {
      if (!isSupportedEntity(message.entity)) return;
      scene.setEntity(message.entity);
      builder.confirm(message.entity.id);
      return;
    }
    case 'delete_entity': {
      scene.removeEntity(message.id);
      return;
    }
    case 'player_state': {
      const state = message.player;
      if (state.playerId === self?.playerId) return;
      avatars.update(state);
      if (!peers.has(state.playerId)) {
        peers.set(state.playerId, {
          playerId: state.playerId,
          name: state.name,
          color: state.color,
        });
        hud.renderPlayers(self, peers.values());
      }
      return;
    }
    case 'peer_join': {
      peers.set(message.peer.playerId, message.peer);
      hud.renderPlayers(self, peers.values());
      hud.toast(t('toast.peerJoined', { name: message.peer.name }));
      return;
    }
    case 'peer_leave': {
      const peer = peers.get(message.playerId);
      peers.delete(message.playerId);
      avatars.remove(message.playerId);
      hud.renderPlayers(self, peers.values());
      if (peer) hud.toast(t('toast.peerLeft', { name: peer.name }));
      return;
    }
    case 'error': {
      hud.toast(message.message, 'error');
      builder.rollback(message.refId);
      if (message.refId) rollbackPendingTexture(message.refId);
      return;
    }
  }
}

/* ------------------------------------------------------------------ */
/* 接続                                                                */
/* ------------------------------------------------------------------ */

function isSupportedEntity(entity: { type?: unknown }): entity is Entity {
  return entity.type === 'brace' || entity.type === 'stair' || entity.type === 'object';
}

function requestedRoom(): string | null {
  const requested = new URL(window.location.href).searchParams.get('room');
  return requested && ROOM_ID_PATTERN.test(requested) ? requested : null;
}

function currentRoom(): string {
  const requested = requestedRoom();
  if (!requested || !canJoinRoomAsGuest(requested)) return DEFAULT_ROOM;
  return requested;
}

/** 新キーが無ければ旧 SDF陰影トグルを使う。未保存はオン。 */
function readSdfShadowFlag(key: string): boolean {
  const stored = window.localStorage.getItem(key);
  if (stored !== null) return stored !== '0';
  return window.localStorage.getItem(SDF_SHADOWS_STORAGE_KEY) !== '0';
}

function storedName(): string {
  const stored = window.localStorage.getItem(NAME_STORAGE_KEY);
  if (stored && stored.trim().length > 0) return stored;
  const generated = `Player-${Math.random().toString(36).slice(2, 6)}`;
  window.localStorage.setItem(NAME_STORAGE_KEY, generated);
  return generated;
}

function storedSlotIndex(): number | undefined {
  const stored = window.sessionStorage.getItem(SLOT_INDEX_STORAGE_KEY);
  if (!stored) return undefined;
  const value = Number(stored);
  return Number.isInteger(value) ? value : undefined;
}

function storedSlots(): unknown {
  const stored = window.localStorage.getItem(SLOT_STORAGE_KEY);
  if (!stored) return undefined;
  try {
    return JSON.parse(stored) as unknown;
  } catch {
    window.localStorage.removeItem(SLOT_STORAGE_KEY);
    return undefined;
  }
}

function readPoseStore(): Record<string, StoredPose> {
  const raw = window.localStorage.getItem(POSE_STORAGE_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Record<string, StoredPose>;
  } catch {
    window.localStorage.removeItem(POSE_STORAGE_KEY);
    return {};
  }
}

function loadPose(roomId: string): StoredPose | null {
  const pose = readPoseStore()[roomId];
  if (!pose || typeof pose !== 'object') return null;
  const { position, yaw, pitch, flying } = pose;
  if (
    !position ||
    !Number.isFinite(position.x) ||
    !Number.isFinite(position.y) ||
    !Number.isFinite(position.z) ||
    !Number.isFinite(yaw) ||
    !Number.isFinite(pitch)
  ) {
    return null;
  }
  return {
    position: { x: position.x, y: position.y, z: position.z },
    yaw,
    pitch: clampPitch(pitch),
    flying: flying === true,
  };
}

function applyStoredPose(roomId: string): void {
  const pose = loadPose(roomId);
  poseToRestore = pose;
  if (pose) player.setPose(pose.position, pose.yaw, pose.pitch, pose.flying);
  else player.respawn(scene.collision);
}

let lastPoseSaveAt = 0;

function persistPose(force = false): void {
  // ワールド未着のうちはスポーン位置などで保存を潰さない
  if (!worldReady) return;
  const now = performance.now();
  if (!force && now - lastPoseSaveAt < POSE_SAVE_INTERVAL) return;
  lastPoseSaveAt = now;
  const store = readPoseStore();
  store[room] = {
    position: { ...player.position },
    yaw: player.yaw,
    pitch: player.pitch,
    flying: player.flying,
  };
  window.localStorage.setItem(POSE_STORAGE_KEY, JSON.stringify(store));
}

function connect(): void {
  if (connection) boot = new BootSession('reconnect');
  boot.recordConnect();
  hud.setBootTiming(null);
  connection?.dispose();
  worldReady = false;
  scene.clearEntities();
  replaceRoomTextures([]);
  avatars.clear();
  peers.clear();
  self = null;
  hud.setRoom(room);
  hud.setName(playerName);
  hud.renderPlayers(null, []);
  connection = new RoomConnection({
    room,
    name: playerName,
    onMessage: handleMessage,
    onStatus: (status: ConnectionStatus) => {
      if (status === 'open') boot.recordWsOpen();
      hud.setStatus(status);
    },
  });
}

function changeRoom(next: string): void {
  if (!ROOM_ID_PATTERN.test(next)) {
    hud.toast(t('toast.invalidRoom'), 'error');
    return;
  }
  if (!canJoinRoomAsGuest(next)) {
    hud.toast(t('toast.roomRequiresLogin'), 'error');
    hud.setRoom(room);
    return;
  }
  if (next === room) return;
  persistPose(true);
  room = next;
  const url = new URL(window.location.href);
  url.searchParams.set('room', room);
  window.history.replaceState(null, '', url);
  applyStoredPose(room);
  connect();
}

function changeName(next: string): void {
  if (next.length === 0) return;
  playerName = next;
  window.localStorage.setItem(NAME_STORAGE_KEY, playerName);
  hud.setName(playerName);
  connect();
}

/* ------------------------------------------------------------------ */
/* 自分の状態の送信                                                    */
/* ------------------------------------------------------------------ */

let lastSentAt = 0;
let lastSent: PlayerState | null = null;

function currentPlayerState(moving = player.moving): PlayerState {
  return {
    // playerId / name / color はサーバー側で接続情報に差し替えられる
    playerId: '',
    name: '',
    color: '',
    position: { ...player.position },
    yaw: player.yaw,
    pitch: player.pitch,
    moving,
    flying: player.flying,
    sneaking: player.sneaking,
    pending: builder.pendingPoints.length > 0 ? [...builder.pendingPoints] : undefined,
  };
}

function publishPlayerState(now: number, state: PlayerState): void {
  lastSentAt = now;
  lastSent = state;
  connection?.send({ type: 'player_state', player: state });
}

function sendPlayerState(now: number): void {
  // 裏タブのキープアライブで DO を起こし続けない。接続自体は維持する。
  if (document.visibilityState === 'hidden') return;
  if (now - lastSentAt < STATE_INTERVAL) return;

  const state = currentPlayerState();
  const changed =
    lastSent === null ||
    now - lastSentAt > STATE_KEEPALIVE ||
    Math.hypot(
      state.position.x - lastSent.position.x,
      state.position.y - lastSent.position.y,
      state.position.z - lastSent.position.z
    ) > 0.002 ||
    Math.abs(state.yaw - lastSent.yaw) > 0.004 ||
    Math.abs(state.pitch - lastSent.pitch) > 0.004 ||
    state.moving !== lastSent.moving ||
    state.flying !== lastSent.flying ||
    state.sneaking !== lastSent.sneaking ||
    (state.pending?.length ?? 0) !== (lastSent.pending?.length ?? 0);

  if (!changed) return;
  publishPlayerState(now, state);
}

/* ------------------------------------------------------------------ */
/* デバッグ表示                                                        */
/* ------------------------------------------------------------------ */

const COMPASS = ['北 (-Z)', '西 (-X)', '南 (+Z)', '東 (+X)'];
let fps = 0;

function debugText(): string {
  const p = player.position;
  const yawDeg = ((player.yaw * 180) / Math.PI) % 360;
  const facing = COMPASS[Math.round(player.yaw / (Math.PI / 2)) & 3];
  return [
    `FPS ${fps.toFixed(0)}`,
    `XYZ ${p.x.toFixed(2)} / ${p.y.toFixed(2)} / ${p.z.toFixed(2)}`,
    `マス ${Math.floor(p.x)} ${Math.floor(p.y)} ${Math.floor(p.z)}`,
    `向き ${facing}  yaw ${yawDeg.toFixed(1)}°  pitch ${((player.pitch * 180) / Math.PI).toFixed(1)}°`,
    `接地 ${player.onGround ? 'yes' : 'no'}  飛行 ${player.flying ? 'yes' : 'no'}  ダッシュ ${player.sprinting ? 'yes' : 'no'}`,
    `オブジェクト ${scene.entityCount}  当たり判定 ${scene.collision.size}`,
    `描画 ${scene.graphicsQuality.label}  安い候補陰影 ${scene.restir.cheapCandidateVisibility ? 'on' : 'off'}  デノイズ ${scene.restir.denoiserMode}  太陽光 ${scene.restir.sunEnabled ? 'on' : 'off'}  間接光 ${scene.restir.ambientEnabled ? 'on' : 'off'}  太陽SDF ${scene.restir.sunSdfShadowsEnabled ? 'on' : 'off'}  DISDF ${scene.restir.diSdfShadowsEnabled ? 'on' : 'off'}  GISDF ${scene.restirGi.sdfShadowsEnabled ? 'on' : 'off'}  解析枠 ${scene.analyticSdfEnabled ? 'on' : 'off'}  解析BVH ${scene.analyticBvhEnabled ? 'on' : 'off'}  ボクセル球 ${scene.voxelSpheresEnabled ? 'on' : 'off'}  ピンボケ ${scene.depthOfField.enabled ? 'on' : 'off'} 焦点 ${scene.depthOfField.focusDistance.toFixed(1)}  参加者 ${peers.size + (self ? 1 : 0)}  アバター ${avatars.count}`,
    `照準 ${builder.targetSummary()}`,
    lastBoot ? formatBootDebug(lastBoot) : '',
  ]
    .filter((line) => line.length > 0)
    .join('\n');
}

/* ------------------------------------------------------------------ */
/* ゲームループ                                                        */
/* ------------------------------------------------------------------ */

let previousTime = performance.now();
let elapsed = 0;
let fpsAccumulator = 0;
let fpsFrames = 0;
let lastDebugUpdate = 0;

function frame(): void {
  requestAnimationFrame(frame);

  const now = performance.now();
  const dt = Math.min((now - previousTime) / 1000, 0.25);
  previousTime = now;
  elapsed += dt;

  // --- 視点 ---------------------------------------------------------
  const look = input.consumeLook();
  player.yaw += look.yaw;
  player.pitch = clampPitch(player.pitch + look.pitch);

  // --- 移動（重力・当たり判定つき） ---------------------------------
  // ワールド到着前は物理を止める。clearEntities 後に落ちて保存位置が潰れるのを防ぐ。
  if (worldReady) {
    player.update(dt, input.intent(), scene.collision);
  }
  scene.viewGrid.follow(player.position);
  scene.setCamera(player.eyePosition(), player.yaw, player.pitch);

  // --- 照準と長押しリピート -----------------------------------------
  if (worldReady) {
    builder.updateTarget();
    if (input.locked) {
      if (input.useHeld && now >= nextUseAt && builder.repeatsUseOnHold) {
        builder.use();
        nextUseAt = now + REPEAT_INTERVAL;
      }
    }
  }

  // --- 他プレイヤー / 自分の影 --------------------------------------
  avatars.syncLocalPlayer(player.position, player.yaw, {
    sneaking: player.sneaking,
    flying: player.flying,
    moving: player.moving,
  });
  avatars.tick(dt);
  scene.setOccluderBoxes(avatars.bodyAabbs());
  if (worldReady && document.visibilityState !== 'hidden') {
    sendPlayerState(now);
    persistPose();
  }

  // --- 描画 ----------------------------------------------------------
  scene.render(elapsed);
  if (boot.isPending) {
    const report = boot.finish();
    if (report) {
      lastBoot = report;
      logBootReport(report);
      hud.setBootTiming(formatBootMenu(report));
    }
  }

  // --- 統計 ----------------------------------------------------------
  fpsAccumulator += dt;
  fpsFrames++;
  if (fpsAccumulator >= 0.5) {
    fps = fpsFrames / fpsAccumulator;
    fpsAccumulator = 0;
    fpsFrames = 0;
    hud.setFps(fps);
  }
  if (hud.debugVisible && now - lastDebugUpdate > 120) {
    lastDebugUpdate = now;
    hud.setDebugText(debugText());
  }
}

/* ------------------------------------------------------------------ */
/* 起動                                                                */
/* ------------------------------------------------------------------ */

const storedSensitivity = Number(window.localStorage.getItem(SENSITIVITY_STORAGE_KEY));
if (Number.isFinite(storedSensitivity) && storedSensitivity > 0) {
  input.sensitivity = storedSensitivity;
}
hud.setSensitivity(input.sensitivity);
input.invertHotbarScroll = window.localStorage.getItem(INVERT_HOTBAR_SCROLL_STORAGE_KEY) === '1';
hud.setInvertHotbarScroll(input.invertHotbarScroll);
hud.setGraphicsQuality(scene.graphicsQuality.id);
scene.depthOfField.enabled = window.localStorage.getItem(DOF_STORAGE_KEY) === '1';
hud.setDepthOfField(scene.depthOfField.enabled);
scene.restir.sunSdfShadowsEnabled = readSdfShadowFlag(SUN_SDF_SHADOWS_STORAGE_KEY);
scene.restir.diSdfShadowsEnabled = readSdfShadowFlag(DI_SDF_SHADOWS_STORAGE_KEY);
scene.restirGi.sdfShadowsEnabled = readSdfShadowFlag(GI_SDF_SHADOWS_STORAGE_KEY);
hud.setSunSdfShadows(scene.restir.sunSdfShadowsEnabled);
hud.setDiSdfShadows(scene.restir.diSdfShadowsEnabled);
hud.setGiSdfShadows(scene.restirGi.sdfShadowsEnabled);
scene.analyticSdfEnabled = window.localStorage.getItem(SDF_ANALYTIC_STORAGE_KEY) !== '0';
hud.setSdfAnalytic(scene.analyticSdfEnabled);
scene.analyticBvhEnabled = window.localStorage.getItem(SDF_ANALYTIC_BVH_STORAGE_KEY) !== '0';
hud.setSdfAnalyticBvh(scene.analyticBvhEnabled);
scene.voxelSpheresEnabled = window.localStorage.getItem(SDF_VOXEL_SPHERES_STORAGE_KEY) !== '0';
hud.setSdfVoxelSpheres(scene.voxelSpheresEnabled);
scene.restir.sunEnabled = window.localStorage.getItem(SUNLIGHT_STORAGE_KEY) !== '0';
hud.setSunlight(scene.restir.sunEnabled);
scene.restir.ambientEnabled = window.localStorage.getItem(AMBIENT_STORAGE_KEY) !== '0';
hud.setAmbient(scene.restir.ambientEnabled);
scene.restirGi.diffuseGain = readGiSlider(
  window.localStorage.getItem(GI_DIFFUSE_GAIN_STORAGE_KEY),
  GI_DIFFUSE_GAIN_MIN,
  GI_DIFFUSE_GAIN_MAX,
  GI_DIFFUSE_GAIN
);
hud.setGiDiffuseGain(scene.restirGi.diffuseGain);
scene.restir.distanceAmbient = readGiSlider(
  window.localStorage.getItem(GI_DISTANCE_AMBIENT_STORAGE_KEY),
  GI_DISTANCE_AMBIENT_MIN,
  GI_DISTANCE_AMBIENT_MAX,
  GI_DISTANCE_AMBIENT
);
hud.setGiDistanceAmbient(scene.restir.distanceAmbient);
scene.restir.nearAmbient = readGiSlider(
  window.localStorage.getItem(GI_NEAR_AMBIENT_STORAGE_KEY),
  GI_NEAR_AMBIENT_MIN,
  GI_NEAR_AMBIENT_MAX,
  GI_NEAR_AMBIENT
);
hud.setGiNearAmbient(scene.restir.nearAmbient);
scene.restir.denoiserMode = resolveRestirDenoiserMode(window.localStorage.getItem(DENOISER_STORAGE_KEY));
hud.setDenoiser(scene.restir.denoiserMode);
scene.restir.cheapCandidateVisibility = resolveRestirFlag(
  window.localStorage.getItem(RESTIR_CHEAP_VISIBILITY_STORAGE_KEY),
  DEFAULT_RESTIR_CHEAP_CANDIDATE_VISIBILITY
);
hud.setRestirCheapVisibility(scene.restir.cheapCandidateVisibility);
scene.collisionDebug = window.localStorage.getItem(COLLISION_DEBUG_STORAGE_KEY) === '1';
hud.setCollisionDebug(scene.collisionDebug);
hud.setRoom(room);
hud.setName(playerName);
hud.setMode('menu');
hud.renderBuilder(builder);
boot.recordUi(performance.now() - uiStartedAt);

// キャンバスをクリックしたらそのままプレイ開始（マイクラのウィンドウ復帰と同じ感覚）
canvas.addEventListener('click', () => input.requestLock());

// ポインタロック外のモードはブラウザ任せの Esc がないので、ここで遷移させる。
window.addEventListener('keydown', (event) => {
  if (event.defaultPrevented) return;
  const target = event.target as HTMLElement | null;
  const isFormControl =
    target?.tagName === 'INPUT' || target?.tagName === 'SELECT' || target?.tagName === 'TEXTAREA';

  if (clothSimDialog.isOpen && (event.code === 'KeyQ' || event.code === 'Escape')) {
    event.preventDefault();
    clothSimDialog.close();
    if (event.code === 'Escape') {
      hud.setMode('playing');
      return;
    }
    input.requestLock();
    return;
  }

  if (
    gridEditor.isOpen &&
    (event.code === 'KeyG' || event.code === 'KeyQ' || event.code === 'Escape')
  ) {
    event.preventDefault();
    gridEditor.close();
    // Esc で requestPointerLock すると、同じキーで即解除 → メニューに落ちる。
    // プレイ UI に戻してクリックでロックし直す。
    if (event.code === 'Escape') {
      hud.setMode('playing');
      return;
    }
    input.requestLock();
    return;
  }

  if (hud.uiMode === 'controls' && (event.code === 'KeyQ' || event.code === 'Escape')) {
    event.preventDefault();
    hud.setMode('menu');
    return;
  }

  if (
    hud.uiMode === 'graphics' &&
    (event.code === 'KeyV' || event.code === 'KeyQ' || event.code === 'Escape')
  ) {
    event.preventDefault();
    if (target instanceof HTMLElement) target.blur();
    // Esc で requestPointerLock すると、同じキーで即解除 → メニューに落ちる。
    // プレイ UI に戻してクリックでロックし直す。
    if (event.code === 'Escape') {
      hud.setMode('playing');
      return;
    }
    input.requestLock();
    return;
  }

  if (isFormControl) return;

  if (
    event.code === 'KeyG' &&
    !input.locked &&
    hud.uiMode !== 'inventory' &&
    hud.uiMode !== 'graphics' &&
    hud.uiMode !== 'controls' &&
    !clothSimDialog.isOpen
  ) {
    event.preventDefault();
    hud.setMode('grid-editor');
    gridEditor.open();
    return;
  }

  if (
    event.code === 'KeyV' &&
    !input.locked &&
    hud.uiMode !== 'inventory' &&
    hud.uiMode !== 'graphics' &&
    hud.uiMode !== 'controls' &&
    !gridEditor.isOpen &&
    !clothSimDialog.isOpen
  ) {
    event.preventDefault();
    hud.setMode('graphics');
    return;
  }

  if (
    hud.uiMode === 'inventory' &&
    (event.code === 'KeyE' || event.code === 'KeyQ' || event.code === 'Escape')
  ) {
    event.preventDefault();
    // Esc で requestPointerLock すると、同じキーで即解除 → メニューに落ちる。
    // プレイ UI に戻してクリックでロックし直す。
    if (event.code === 'Escape') {
      hud.setMode('playing');
      return;
    }
    input.requestLock();
    return;
  }

  if (hud.uiMode === 'viewing' && event.code === 'Escape') {
    hud.setMode('menu');
  }

  if (hud.uiMode === 'playing' && input.touchPlaying && event.code === 'Escape') {
    event.preventDefault();
    input.releaseLock();
  }
});

function flushPose(): void {
  persistPose(true);
}

function pausePresence(): void {
  flushPose();
  if (!worldReady) return;
  publishPlayerState(performance.now(), currentPlayerState(false));
}

function resumePresence(): void {
  lastSent = null;
  lastSentAt = 0;
}

window.addEventListener('pagehide', flushPose);
window.addEventListener('beforeunload', flushPose);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') pausePresence();
  else resumePresence();
});

if (import.meta.env.DEV) {
  (window as unknown as { __mapeditorDebug: Record<string, unknown> }).__mapeditorDebug = {
    getPosition: () => ({
      ...player.position,
      yaw: player.yaw,
      pitch: player.pitch,
      flying: player.flying,
    }),
    getRoom: () => room,
    isWorldReady: () => worldReady,
    lastBoot: () => lastBoot,
    persistPose: () => persistPose(true),
    setPose: (
      position: { x: number; y: number; z: number },
      yaw = 0,
      pitch = 0,
      flying = false
    ) => {
      player.setPose(position, yaw, pitch, flying);
      persistPose(true);
    },
    lastReadyPose: null,
  };
}

connect();
frame();
